// $lib/actions/auth.js
import ApiClient from '$lib/api';
import { user, accessToken } from '$lib/stores/store';
import { clearRefreshToken } from '$lib/db/indexeddb'; // 📌 thêm dòng này
import { closeSocket } from '$lib/websocket-client';

const api = new ApiClient();
const DEFAULT_STUN_URL = import.meta.env.VITE_STUN_URL || 'stun:stun.l.google.com:19302';
const DEFAULT_ROUTE_PREFERENCE = ['host', 'srflx', 'relay'];

export async function logOut() {
    try {
        const data = await api.post('/auth/logout', {}, { withCredentials: true });
        user.set(null);
        accessToken.set(null);

        // close socket
        await closeSocket();

        // Xoá refresh token khỏi IndexedDB
        await clearRefreshToken();

        console.log('🔐 Logged out + refresh token cleared');

    } catch (err) {
        console.error('🚨 LOGOUT ERROR:', err);
    }
}

/**
 * 🔐 GET TURN ICE CONFIG (Dynamic - use-auth-secret)
 * Backend: GET /api/turn
 * - Auto attach Bearer token
 * - Auto refresh nếu 401
 * - Bảo vệ TURN server khỏi bị abuse
 */
export async function getTurnConfig({ iceTransportPolicy = 'all' } = {}) {
    const requestedPolicy = normalizeIceTransportPolicy(iceTransportPolicy);
    try {
        const res = await api.get('/api/turn/auth');

        // Backend trả: { success: true, data: { iceServers: [...] } }
        if (!Array.isArray(res?.data?.iceServers)) {
            throw new Error('Invalid TURN response');
        }

        const effectivePolicy = normalizeIceTransportPolicy(res.data.iceTransportPolicy || requestedPolicy);
        const iceServers = normalizeIceServers(res.data.iceServers, { ensureStun: effectivePolicy !== 'relay' });
        return {
            ...res.data,
            iceServers,
            iceTransportPolicy: effectivePolicy,
            routePreference: normalizeRoutePreference(res.data.routePreference),
            relayRequired: effectivePolicy === 'relay' || Boolean(res.data.relayRequired)
        }; // { iceServers, ttl, realm, iceTransportPolicy, relayAvailable }
    } catch (err) {
        console.error('🚨 GET TURN CONFIG ERROR:', err);

        // Relay-only là policy bắt buộc: không fallback sang STUN vì sẽ sai contract cuộc gọi.
        if (requestedPolicy === 'relay') {
            return {
                iceServers: [],
                iceTransportPolicy: 'relay',
                relayRequired: true,
                relayAvailable: false,
                error: 'TURN_UNAVAILABLE'
            };
        }

        // Fallback: chỉ STUN nếu TURN lỗi (tránh crash call)
        return {
            iceTransportPolicy: 'all',
            relayRequired: false,
            relayAvailable: false,
            routePreference: DEFAULT_ROUTE_PREFERENCE,
            iceServers: [
                { urls: [DEFAULT_STUN_URL] }
            ]
        };
    }
}

function normalizeIceTransportPolicy(value) {
    const policy = String(value || 'all').trim().toLowerCase();
    return policy === 'relay' || policy === 'relay-only' ? 'relay' : 'all';
}

function normalizeIceServers(iceServers = [], { ensureStun = true } = {}) {
    const normalized = iceServers
        .map((server) => {
            const urls = normalizeUrls(server?.urls);
            if (!urls.length) return null;
            return { ...server, urls };
        })
        .filter(Boolean);

    if (ensureStun && !normalized.some((server) => hasIceUrl(server, 'stun:'))) {
        normalized.unshift({ urls: [DEFAULT_STUN_URL] });
    }

    return normalized;
}

function normalizeUrls(urls) {
    const list = Array.isArray(urls) ? urls : [urls];
    return [...new Set(list.map((url) => String(url || '').trim()).filter(Boolean))];
}

function hasIceUrl(server, prefix) {
    return normalizeUrls(server?.urls).some((url) => url.toLowerCase().startsWith(prefix));
}

function normalizeRoutePreference(value) {
    const list = Array.isArray(value) ? value : DEFAULT_ROUTE_PREFERENCE;
    const normalized = list
        .map((item) => String(item || '').trim().toLowerCase())
        .filter((item) => DEFAULT_ROUTE_PREFERENCE.includes(item));
    return normalized.length ? [...new Set(normalized)] : DEFAULT_ROUTE_PREFERENCE;
}
